<script lang="ts" setup>
  import Dropdown from '@leaflink/stash/Dropdown.vue';
  import useModals from '@leaflink/stash/useModals.vue';
  import {
    KnockFeedProvider,
    NotificationEventNames,
    NotificationEventProperties,
    NotificationFeedPopover,
    NotificationIconButton,
    NotificationToaster,
  } from '@leaflink/vue-notification-feed';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';

  import LanguagePicker from '@/components/LanguagePicker/LanguagePicker.vue';
  import UserProfile from '@/components/layout/UserProfile/UserProfile.vue';
  import MiniCart from '@/components/MiniCart/MiniCart.vue';
  import PostEnrollmentDropdown from '@/components/PaymentOnSellThroughEnrollment/PostEnrollmentDropdown.vue';
  import ShareMenuModal from '@/components/ShareMenuModal/ShareMenuModal.vue';
  import { ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
  import { env } from '@/constants/env';
  import { useAuthStore } from '@/piniaStores/auth/auth.store';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { track } from '@/track';

  import CompanyStatus from './CompanyStatus';
  import StoreFavoritesNavIcon from './StoreFavoritesNavIcon';

  const modals = useModals();
  const router = useRouter();

  const { activeCompany: company, isAppLoaded: isDataLoaded } = storeToRefs(useCompanyStore());

  const {
    getIsSplitEnabled: isSplitEnabled,
    user,
    userActiveCompanyPermissions: permissions,
  } = storeToRefs(useUserStore());

  const availableLanguages = computed(() => {
    return company.value?.availableLanguagesTranslated || [];
  });
  const settingsUrl = computed(() => {
    const { fullPath } = router.resolve({ name: ROUTE_NAMES.NOTIFICATION_PREFERENCES });

    return fullPath;
  });

  const canPurchase = computed(() => permissions.value?.canPurchase);

  const authStore = useAuthStore();

  const isNotificationsEnabled = computed(() => {
    return isSplitEnabled.value(SPLIT_NAMES.KNOCK_NOTIFICATIONS);
  });

  const isNotificationToasterEnabled = computed(() => {
    return isSplitEnabled.value(SPLIT_NAMES.KNOCK_NOTIFICATIONS_TOASTER);
  });

  const isPaymentOnSellThroughEnrollmentEnabled = computed(() => {
    return permissions.value?.staffIsAdmin && isSplitEnabled.value(SPLIT_NAMES.PAYMENT_ON_SELL_THROUGH_ENROLLMENT);
  });

  const isShareMenuModalEnabled = computed(() => {
    return isSplitEnabled.value(SPLIT_NAMES.ADS_ENABLE_SHARE_MENU);
  });

  const openShareMenuModal = () => {
    if (!isShareMenuModalEnabled.value) {
      return;
    }

    modals.open({
      component: ShareMenuModal,
      attributes: {
        modalSource: 'profile-dropdown',
        onDismiss: () => {
          modals.close();
        },
      },
    });
  };

  const trackNotificationEvent = (eventName: NotificationEventNames, properties: NotificationEventProperties) => {
    track(eventName, properties);
  };
</script>

<template>
  <header :class="$style.header">
    <CompanyStatus />

    <nav class="tw-flex tw-items-center" role="navigation">
      <ul :class="$style.nav" class="tw-flex tw-items-center tw-gap-6">
        <li v-if="isPaymentOnSellThroughEnrollmentEnabled" data-test="app-header|post-enrollment" class="tw-self-end">
          <PostEnrollmentDropdown />
        </li>
        <li v-if="isNotificationsEnabled && authStore.userId" data-test="app-header|notifications">
          <KnockFeedProvider
            :api-key="env.VITE_KNOCK_API_KEY"
            :feed-id="env.VITE_KNOCK_FEED_ID"
            :user-id="authStore.userId"
            :user-token="authStore.knockUserToken"
            @notification-event="trackNotificationEvent"
          >
            <Dropdown
              close-manually
              class="!tw-block"
              content-class="!tw-z-dialog"
              :reattach="false"
              data-test="app-header|notification-dropdown"
            >
              <template #toggle="{ toggle, isActive }">
                <NotificationIconButton
                  data-test="app-header|notification-button"
                  :is-active="isActive"
                  @click="toggle"
                />
              </template>
              <NotificationFeedPopover :settings-path="settingsUrl" data-test="app-header|notification-feed" />
            </Dropdown>
            <NotificationToaster v-if="isNotificationToasterEnabled" />
          </KnockFeedProvider>
        </li>
        <li v-if="isDataLoaded && canPurchase" :class="$style.navIcon" data-test="store-favorite-nav-icon">
          <StoreFavoritesNavIcon />
        </li>
        <li v-if="isDataLoaded && availableLanguages.length > 1" :class="$style.navItem">
          <LanguagePicker :language="user.language" :languages="availableLanguages" />
        </li>
        <li v-if="isDataLoaded && canPurchase && company" :class="$style.navItem">
          <MiniCart :company-slug="company.slug" :is-company-buyer="company.buyer" />
        </li>
        <li v-if="user && company" :class="$style.navItem">
          <UserProfile :user="user" :company="company" @open-share-menu-modal="openShareMenuModal" />
        </li>
      </ul>
    </nav>
  </header>
</template>

<style lang="scss" module>
  .header {
    // .top-header
    @include elevation('high');

    align-items: center;
    background-color: var(--color-white);
    border: none;
    display: flex;
    height: $top-nav-height;
    justify-content: flex-end;
    padding: 6px 12px;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: z-index('page');

    :global(.icon) {
      vertical-align: middle;
    }

    @include breakpoint('xl') {
      width: calc(100% - $sidebar-width);
      display: grid;
      grid-template-columns: 1fr min-content;
      left: var(
        --sidebar-width
      ); // this is redundant with "grid-area: header" in layout.scss, but grid-area does not work with "position: fixed" in Safari and Firefox at the time of this writing
    }
  }

  .nav {
    list-style: none;
    margin: 0;
    padding: 0;

    img {
      vertical-align: middle;
    }
  }

  .navItem svg {
    height: 20px;
    vertical-align: bottom;
    width: 20px;
  }
</style>
